import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, Edit, Plus } from 'lucide-react';
import { useAuth } from '../../../../../../contexts/AuthContext';
import { itemsService } from '../../../../../../services/itemsService';
import { stcBinGrpService } from '../../../../../../services/stcBinGrpService';
import reportConfigurationService from '../../../../../../services/reportConfigurationService';
import { stcMovementTypeService } from '../../../../../../services/stcMovementTypeService';
import { stcSortingBoxDefService } from '../../../../../../services/stcSortingBoxDefService';
import { ItemsDto, EnrichedItem } from '../../../../../../types/items';
import { MasterDataLookups } from './types';
import { ItemsBulkUpdateModal } from './ItemsBulkUpdateModal';
import { WarehouseTable } from '../../../../../shared/table';
import { createColumnFactory } from '../../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition } from '../../../../../shared/table/types/table';

export const ItemsDataTable_MIGRATED: React.FC = () => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // Core data state
  const [items, setItems] = useState<ItemsDto[]>([]);
  const [enrichedItems, setEnrichedItems] = useState<EnrichedItem[]>([]);
  const [masterData, setMasterData] = useState<MasterDataLookups | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItemNos, setSelectedItemNos] = useState<string[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  // Create the column factory
  const columnFactory = createColumnFactory<EnrichedItem>();

  // Load data
  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch items and all related master data in parallel
      const [
        itemsData,
        binGroupsData,
        reportsData,
        movementTypesData,
        sortingBoxesData
      ] = await Promise.all([
        itemsService.getCurrentItems(),
        stcBinGrpService.getCurrent(),
        reportConfigurationService.getAll(companyNo),
        stcMovementTypeService.getCurrent(),
        stcSortingBoxDefService.getCurrent()
      ]);

      // Create hardcoded pick rules lookup
      const pickRulesMap = new Map<number, { pickRuleNo: number; description: string }>([
        [1, { pickRuleNo: 1, description: 'FIFO' }],
        [2, { pickRuleNo: 2, description: 'Antall' }],
        [3, { pickRuleNo: 3, description: 'Prioritet' }],
        [4, { pickRuleNo: 4, description: 'Batch fra ERP' }]
      ]);

      // Create hardcoded item types lookup
      const itemTypesMap = new Map<number, { itemType: number; description: string }>([
        [0, { itemType: 0, description: 'Standard' }],
        [1, { itemType: 1, description: 'Batchnr' }],
        [2, { itemType: 2, description: 'Serienummer' }],
        [3, { itemType: 3, description: 'Stand005' }],
        [4, { itemType: 4, description: 'Batch med holdbarhet' }]
      ]);

      // Create lookup maps for O(1) foreign key resolution
      const lookups: MasterDataLookups = {
        binGrps: new Map(binGroupsData.map(g => [g.binGrpNo, g])),
        reports: new Map(reportsData.map(r => [r.reportID, r])),
        movementTypes: new Map(movementTypesData.map(m => [m.movementTypeNo, m])),
        sortingBoxes: new Map(sortingBoxesData.map(s => [s.stcBoxTypeID, s])),
        pickRules: pickRulesMap,
        itemTypes: itemTypesMap
      };

      // Enrich items with related entity descriptions
      const enriched: EnrichedItem[] = itemsData.map(item => ({
        ...item,
        binGrpDescription: item.binGrpNo ? lookups.binGrps.get(item.binGrpNo)?.description1 : undefined,
        reportName: item.reportID ? lookups.reports.get(item.reportID)?.reportName : undefined,
        itemTypeText: item.itemType !== undefined ? lookups.itemTypes.get(item.itemType)?.description : undefined,
        pickRuleText: item.pickRuleNo ? lookups.pickRules.get(item.pickRuleNo)?.description : undefined,
        movementTypeDescription: item.movementTypeNo ? lookups.movementTypes.get(item.movementTypeNo)?.description1 : undefined,
        stcBoxTypeDescription: item.stcBoxTypeID ? lookups.sortingBoxes.get(item.stcBoxTypeID)?.description1 : undefined
      }));


      setItems(itemsData);
      setEnrichedItems(enriched);
      setMasterData(lookups);

    } catch (err) {
      console.error('Error loading items data:', err);
      setError('Kunne ikke laste varedata. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [companyNo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Column definitions using the factory
  const columns: ColumnDefinition<EnrichedItem>[] = useMemo(() => [
    // Core item info - key columns with responsive widths
    columnFactory.createTextColumn('itemNo', 'itemNo', 'Varenr.', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createTextColumn('description1', 'description1', 'Beskrivelse', {
      minWidth: 150,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('itemBarCode', 'itemBarCode', 'GTIN', {
      minWidth: 120,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('supplierItemNo', 'supplierItemNo', 'Levr. varenr.', {
      minWidth: 120,
      sortable: true,
      filterType: 'both'
    }),

    // Lookup fields with enriched display
    columnFactory.createTextColumn('binGrpNo', 'binGrpNo', 'Lokasjonsgruppe', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedItem) => {
        return item.binGrpDescription || String(value || '-');
      }
    }),

    columnFactory.createTextColumn('pickRuleNo', 'pickRuleNo', 'Plukkregel', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedItem) => {
        return item.pickRuleText || String(value || '-');
      }
    }),

    columnFactory.createTextColumn('stcBoxTypeID', 'stcBoxTypeID', 'Sorteringskasse', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedItem) => {
        return item.stcBoxTypeDescription || String(value || '-');
      }
    }),

    columnFactory.createTextColumn('movementTypeNo', 'movementTypeNo', 'Bevegelseskode', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedItem) => {
        return item.movementTypeDescription || String(value || '-');
      }
    }),

    columnFactory.createTextColumn('reportID', 'reportID', 'Standard etikett', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedItem) => {
        return item.reportName || String(value || '-');
      }
    }),

    columnFactory.createTextColumn('itemType', 'itemType', 'Varetype', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedItem) => {
        return item.itemTypeText || String(value || '-');
      }
    }),

    // Boolean fields - these can be narrower since they only show Ja/Nei
    columnFactory.createTextColumn('putAwayToPickableBinYesNo', 'putAwayToPickableBinYesNo', 'Mottak til plukklok.', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value === true ? 'Ja' : value === false ? 'Nei' : '-'
    }),

    columnFactory.createTextColumn('stcTransactionsYesNo', 'stcTransactionsYesNo', 'Lagerstyring', {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value === true ? 'Ja' : value === false ? 'Nei' : '-'
    }),

    columnFactory.createTextColumn('newLineYesNo', 'newLineYesNo', 'Tillegg J/N', {
      minWidth: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value === true ? 'Ja' : value === false ? 'Nei' : '-'
    }),

    columnFactory.createTextColumn('useSharedBinYesNo', 'useSharedBinYesNo', 'Kan brukes på felles lok.', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value === true ? 'Ja' : value === false ? 'Nei' : '-'
    }),

    columnFactory.createTextColumn('useMaxReplenishQtyYesNo', 'useMaxReplenishQtyYesNo', 'Bruk maks påfylling', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value === true ? 'Ja' : value === false ? 'Nei' : '-'
    }),

    // Group fields - these can be compact as they're usually short codes
    columnFactory.createTextColumn('itemGrp1', 'itemGrp1', 'VareGrp. 1', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('itemGrp2', 'itemGrp2', 'VareGrp. 2', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('itemGrp3', 'itemGrp3', 'VareGrp. 3', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    // Free text fields - flexible content, allow to grow
    columnFactory.createTextColumn('freeTxt1', 'freeTxt1', 'Fritekst 1', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('freeTxt2', 'freeTxt2', 'Fritekst 2', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('freeTxt3', 'freeTxt3', 'Fritekst 3', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('freeTxt4', 'freeTxt4', 'Fritekst 4', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('freeTxt5', 'freeTxt5', 'Fritekst 5', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('freeTxt6', 'freeTxt6', 'Fritekst 6', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('freeTxt7', 'freeTxt7', 'Fritekst 7', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('freeTxt8', 'freeTxt8', 'Fritekst 8', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('freeTxt9', 'freeTxt9', 'Fritekst 9', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('freeTxt10', 'freeTxt10', 'Fritekst 10', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),

    // Other fields
    columnFactory.createTextColumn('itemPictureName', 'itemPictureName', 'Bildenavn', {
      minWidth: 120,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('stdLocationName', 'stdLocationName', 'Standard Lok.', {
      minWidth: 120,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createTextColumn('stdBinCode', 'stdBinCode', 'Fast Plukklokasjon', {
      minWidth: 130,
      sortable: true,
      filterType: 'both'
    }),

    // Number fields
    columnFactory.createNumberColumn('maxReplenishQty', 'maxReplenishQty', 'Maks påfyllingsantall', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value ? Number(value).toLocaleString('no-NO') : '-'
    })
  ], [columnFactory, masterData]);

  // Handle bulk operations (now with row selection)
  const handleBulkEdit = useCallback(() => {
    if (selectedItemNos.length === 0) {
      window.alert('Velg minst én vare for å redigere');
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedItemNos.length]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedItemNos.length === 0) {
      window.alert('Velg minst én vare for å slette');
      return;
    }

    if (!window.confirm(`Er du sikker på at du vil slette ${selectedItemNos.length} vare(r)?`)) {
      return;
    }

    try {
      setIsLoading(true);

      for (const itemNo of selectedItemNos) {
        await itemsService.deleteItem(companyNo, itemNo);
      }

      setSelectedItemNos([]);
      await loadData();
      alert(`${selectedItemNos.length} vare(r) slettet`);
    } catch (err) {
      console.error('Error deleting items:', err);
      alert('Kunne ikke slette varer. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedItemNos, companyNo, loadData]);

  const handleCreateNew = useCallback(() => {
    // TODO: Implement create new functionality
    alert('Opprett ny funksjonalitet kommer snart...');
  }, []);

  const handleUpdateSuccess = useCallback(() => {
    loadData();
    setSelectedItemNos([]);
    setShowBulkEditModal(false);
  }, [loadData]);

  const handleDeleteSuccess = useCallback(() => {
    loadData();
    setSelectedItemNos([]);
    setShowBulkEditModal(false);
  }, [loadData]);

  // Handle showing legacy version
  const handleShowLegacy = useCallback(() => {
    // Toggle back to legacy version by updating the WarehouseMaintenance component
    window.location.hash = 'show-legacy-items';
    window.location.reload();
  }, []);

  // Custom row ID function for items
  const getItemRowId = useCallback((item: EnrichedItem) => item.itemNo, []);

  // Handle selection change
  const handleSelectionChange = useCallback((selectedRows: (string | number)[], selectedData: EnrichedItem[]) => {
    setSelectedItemNos(selectedRows as string[]);
  }, []);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'show-legacy',
      label: 'Vis legacy versjon (sammenligning)',
      icon: Edit,
      onClick: () => handleShowLegacy(),
      variant: 'secondary' as const
    },
    {
      id: 'bulk-edit',
      label: `Vedlikehold (${selectedItemNos.length})`,
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: (data: EnrichedItem[], selectedRows?: EnrichedItem[]) => selectedItemNos.length === 0,
      variant: 'primary' as const
    },
    {
      id: 'bulk-delete',
      label: `Slett (${selectedItemNos.length})`,
      icon: Edit,
      onClick: () => handleBulkDelete(),
      disabled: (data: EnrichedItem[], selectedRows?: EnrichedItem[]) => selectedItemNos.length === 0,
      variant: 'danger' as const
    },
    {
      id: 'create',
      label: 'Opprett ny',
      icon: Plus,
      onClick: () => handleCreateNew(),
      variant: 'primary' as const
    },
    {
      id: 'refresh',
      label: 'Oppdater',
      icon: RefreshCw,
      onClick: () => loadData(),
      variant: 'secondary' as const
    }
  ], [handleShowLegacy, handleBulkEdit, handleBulkDelete, handleCreateNew, loadData, selectedItemNos.length]);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <WarehouseTable
          data={enrichedItems}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            },
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'items-data-table'
          }}
          selectedRows={selectedItemNos}
          onSelectionChange={handleSelectionChange}
          getRowId={getItemRowId}
          tableActions={tableActions}
          loading={isLoading}
          error={error || undefined}
        />

      {/* Bulk Update Modal */}
      {showBulkEditModal && selectedItemNos.length > 0 && (
        <ItemsBulkUpdateModal
          isOpen={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          selectedItemNos={selectedItemNos}
          companyNo={companyNo}
          onUpdateSuccess={handleUpdateSuccess}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};